import { Link, useNavigate, useParams } from 'react-router-dom';
import type { InvoiceDto } from '@/api/generated';
import { useInvoices } from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { Icon } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { Pill, type PillKind } from '@/design-system/primitives/Pill/Pill';
import { budgetPostBySlug } from '@/domain/budgetPosts';
import { formatNumberNlBE, toNumber } from '@/lib/format';
import styles from './BudgetPostDetail.module.css';

// BF-13 — drilldown page for /budget/post/:postSlug. Lists every invoice
// booked against the post, with totals at the top. Empty state explains the
// "categorize an invoice" flow when no invoices have that post yet.

const INVOICE_STATUS_KIND: Record<string, PillKind> = {
  Open: 'warn',
  Paid: 'ok',
  Clarification: 'info',
  Objected: 'bad',
};

export function BudgetPostDetail() {
  const { postSlug } = useParams<{ postSlug: string }>();
  const navigate = useNavigate();
  const post = postSlug ? budgetPostBySlug(postSlug) : undefined;

  // Always call hooks unconditionally — pass an empty filter when the slug
  // doesn't resolve so React's hook rules aren't violated; we early-return
  // below.
  const invoices = useInvoices(post ? { budgetPost: post.id, pageSize: 100 } : { pageSize: 1 });

  if (!post) {
    return (
      <AppFrame>
        <div className={styles.headerRow}>
          <div>
            <div className="eyebrow">Budget · onbekende post</div>
            <h1 className={styles.title}>Post niet gevonden</h1>
          </div>
          <Btn variant="ghost" onClick={() => navigate('/budget')}>
            <Icon name="chevR" size={14} /> Terug naar budget
          </Btn>
        </div>
        <Card className={styles.emptyCard}>
          <p>De post "{postSlug}" bestaat niet. Mogelijk is de link verouderd.</p>
        </Card>
      </AppFrame>
    );
  }

  return (
    <AppFrame>
      <div className={`row ${styles.headerRow}`}>
        <div>
          <div className="eyebrow">Budget · {post.label}</div>
          <h1 className={styles.title}>{post.label}</h1>
        </div>
        <div className={`ml-auto row gap-8`}>
          <Btn variant="ghost" onClick={() => navigate('/budget')}>
            <Icon name="chevR" size={14} /> Terug naar budget
          </Btn>
        </div>
      </div>

      <QueryBoundary query={invoices} loading="Facturen laden…">
        {(page) => {
          const items = page.items;
          const total = items.reduce((s, i) => s + toNumber(i.amount), 0);
          const openTotal = items
            .filter((i) => i.status !== 'Paid')
            .reduce((s, i) => s + toNumber(i.amount), 0);
          const paidTotal = total - openTotal;

          return (
            <>
              <div className={styles.kpiRow}>
                <Card className={styles.kpi}>
                  <div className="eyebrow">Totaal geboekt</div>
                  <div className={styles.kpiValue}>€ {formatNumberNlBE(total)}</div>
                  <div className={styles.kpiSub}>{items.length} facturen</div>
                </Card>
                <Card className={styles.kpi}>
                  <div className="eyebrow">Openstaand</div>
                  <div className={styles.kpiValue}>€ {formatNumberNlBE(openTotal)}</div>
                </Card>
                <Card className={styles.kpi}>
                  <div className="eyebrow">Betaald</div>
                  <div className={styles.kpiValue}>€ {formatNumberNlBE(paidTotal)}</div>
                </Card>
              </div>

              <Card className={styles.listCard}>
                {items.length === 0 ? (
                  <div className={styles.empty}>
                    <p>Nog geen facturen onder deze post.</p>
                    <p className={styles.emptyHint}>
                      Maak een factuur aan via Budget → Factuur en selecteer "{post.label}" als
                      post.
                    </p>
                  </div>
                ) : (
                  items.map((i, idx) => (
                    <InvoiceRow key={i.id} invoice={i} last={idx === items.length - 1} />
                  ))
                )}
              </Card>
            </>
          );
        }}
      </QueryBoundary>
    </AppFrame>
  );
}

function InvoiceRow({ invoice, last }: { invoice: InvoiceDto; last: boolean }) {
  const amount = toNumber(invoice.amount);
  return (
    <Link
      to={`/budget/factuur/${invoice.id}`}
      className={`row gap-12 ${styles.row} ${last ? styles.rowLast : ''}`}
    >
      <Pill kind={INVOICE_STATUS_KIND[invoice.status] ?? 'idle'} dot={false}>
        {invoice.status}
      </Pill>
      <span className={styles.supplier}>{invoice.supplier}</span>
      <span className={`mono ml-auto ${styles.amount}`}>€ {formatNumberNlBE(amount)}</span>
      <span className={`mono ${styles.due}`}>{invoice.dueAt}</span>
    </Link>
  );
}
